import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {Router, RouterForm, RouterService, RouterType} from "../../../../../../../services/back/router.service";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {SnackbarService} from "../../../../../../../services/front/snackbar.service";
import {distinctUntilChanged, finalize, forkJoin, Observable, of, Subscription, switchMap, takeWhile, tap} from "rxjs";
import {ipAddressValidator} from "../../../../../../../utils/forms.utils";
import {filter, map} from "rxjs/operators";
import {RouterDetailData, RouterDetailService} from "../../../../../../../services/front/router-detail.service";
import {DesignSystemModule} from "../../../../../../design-system/design-system.module";
import {CriticalityLevel} from "../../../../../../../services/tenant.service";
import {ServerService} from "../../../../../../../services/back/server.service";

@Component({
  selector: 'app-router-information-form',
  standalone: true,
	imports: [
		CommonModule,
		DesignSystemModule,
		TranslateModule
	],
  templateUrl: './router-information-form.component.html',
  styleUrl: './router-information-form.component.scss'
})
export class RouterInformationFormComponent implements OnInit, OnDestroy{
	@Input() router: Router;

	tenantId: string;
	_saving: boolean;
	isEditor: boolean;

	initialized: boolean = false;

	advancementPercent: number = 0;

	routerTypeList: RouterType[] = Object.values(RouterType);

	formGroup: FormGroup;

	locationSearchControl: FormControl<string | null> = new FormControl<string | null>('');
	locationList: string[] = [];
	filteredLocationList: string[] = [];

	criticalityControl: FormControl<CriticalityLevel | null>;

	subscription: Subscription = new Subscription();

	constructor(private routerService: RouterService,
				private translateService: TranslateService,
				private snackBarService: SnackbarService,
				private serverService: ServerService,
				private routerDetailService: RouterDetailService) {
	}

	ngOnInit() {
		this.subscription.add(this.routerDetailService.getRouterDetailDataChanges()
			.pipe(tap(data => this.setRouterDetailData(data)))
			.subscribe(() => this.initialize()));

		this.buildForm();
		const values: any[] = [this.router.ipAddress, this.router.type, this.router.location];
		this.advancementPercent = (values.filter(v => !!v).length / values.length) * 100;
	}

	setRouterDetailData(data: RouterDetailData): void {
		this.tenantId = data.tenantId;
		this.isEditor = data.isEditor;
	}

	initialize(): void {
		this.subscription.add(
			forkJoin([
				this.routerService.getRouterCriticality(this.tenantId, this.router.id),
				this.serverService.getInfrastructureLocationList(this.tenantId)
			])
			.pipe(
				tap(([criticality, locationList ]) => {
					this.criticalityControl.setValue(criticality, { emitEvent: false });
					this.locationList = locationList;
					this.filteredLocationList = this.locationList;
				})
			)
			.subscribe(() => this.initialized = true));

		this.subscription.add(this.routerService.getRouterCriticality(this.tenantId, this.router.id)
			.pipe(tap((criticality) => this.criticalityControl.setValue(criticality, { emitEvent: false })))
			.subscribe(() => this.initialized = true));
	}

	buildForm(): void {
		this.formGroup = new FormGroup({
			type: new FormControl<RouterType>(this.router.type, [Validators.required]),
			ipAddress: new FormControl<string | null>(this.router.ipAddress, [ipAddressValidator()]),
			location: new FormControl<string | null>(this.router.location)
		});

		this.subscription.add(this.locationSearchControl.valueChanges.pipe(distinctUntilChanged())
			.subscribe(text => this.filterLocationList(text)));

		this.criticalityControl = new FormControl<CriticalityLevel | null>(null);

		this.subscription.add(this.locationFormControl.valueChanges.pipe(distinctUntilChanged())
			.subscribe(() => this.updateRouterInformation()));

		this.subscription.add(this.typeFormControl.valueChanges.pipe(distinctUntilChanged())
			.subscribe(() => this.updateRouterInformation()));
	}

	filterLocationList(text?: string | null) {
		this.filteredLocationList = this.locationList
			.filter(value => !text || value.toLowerCase().includes(text.toLowerCase()))
			.sort((a, b) => a.localeCompare(b));
	}

	createLocation(location: string): void {
		this.locationFormControl.setValue(location);
	}

	updateRouterInformation(): void {
		this.switchSaving()
			.pipe(
				filter(() => this.hasInformationChanged() && this.formGroup.valid),
				map(() => this.buildRouterForm()),
				switchMap(form => this.routerService.update(this.tenantId, this.router.id, form)),
				tap((updateSucceeded) => this.displayUpdatedSucceed(updateSucceeded)),
				tap(() => this.routerDetailService.refreshRouter()),
				takeWhile(() => !this.subscription.closed),
				finalize(() => this.switchSaving()))
			.subscribe();
	}

	hasInformationChanged(): boolean {
		return this.router.type !== (this.typeFormControl.value)
			|| this.router.ipAddress !== (this.ipAddressFormControl.value || null)
			|| this.router.location !== (this.locationFormControl.value || null);
	}

	buildRouterForm(): RouterForm {
		return {
			name: this.router.name,
			type: this.typeFormControl.value,
			ipAddress: this.ipAddressFormControl.value || undefined,
			location: this.locationFormControl.value || undefined,
			description: this.router.description || undefined,
			internetConnection: this.router.internetConnection || undefined
		};
	}

	displayUpdatedSucceed(updateSucceed: boolean): void {
		if (updateSucceed) {
			this.snackBarService.show(this.translateService.instant('page.application.detail.update.success'));
		} else {
			this.snackBarService.show(this.translateService.instant('page.application.detail.update.fail'), 'danger-snack');
		}
	}

	switchSaving(): Observable<{}> {
		this._saving = !this._saving;
		return of({});
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}

	get typeFormControl(): FormControl {
		return this.formGroup.get('type') as FormControl;
	}

	get ipAddressFormControl(): FormControl {
		return this.formGroup.get('ipAddress') as FormControl;
	}

	get locationFormControl(): FormControl {
		return this.formGroup.get('location') as FormControl;
	}
}
